import React, {useState, useMemo} from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {vscDarkPlus} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {Button} from 'reactstrap';
import Icon from '../shared/Icon.tsx';
import {DownloadIcon} from 'lucide-react';

interface HtmlPageCodeBlockProps {
    value: string;
    filename?: string;
}

const HtmlPageCodeBlock: React.FC<HtmlPageCodeBlockProps> = ({
    value,
    filename = 'vibe.html'
}) => {
    const [downloaded, setDownloaded] = useState(false);

    // Check if HTML contains closing </html> tag
    const isValidHtml = useMemo(() => {
        return value.includes('</html>');
    }, [value]);

    const downloadHtml = () => {
        if (!isValidHtml) {
            return;
        }

        const blob = new Blob([value], {type: 'text/html'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setDownloaded(true);
        setTimeout(() => setDownloaded(false), 2000);
    };

    return (
        <>
            <div className="d-flex justify-content-end gap-2 mb-2">
                <Button
                    color="secondary"
                    size="sm"
                    onClick={downloadHtml}
                    disabled={!isValidHtml}
                    style={{
                        backgroundColor: downloaded ? '#28a745' : undefined,
                        borderColor: downloaded ? '#28a745' : undefined
                    }}
                    title={
                        !isValidHtml
                            ? 'HTML must contain closing </html> tag'
                            : undefined
                    }
                >
                    <Icon icon={DownloadIcon} className="me-1" />
                    {downloaded ? 'Downloaded!' : 'Download'}
                </Button>
            </div>
            {!isValidHtml && (
                <div className="alert alert-warning mb-2" role="alert">
                    <small>
                        Download is disabled until HTML contains a closing
                        &lt;/html&gt; tag
                    </small>
                </div>
            )}
            <SyntaxHighlighter language="html" style={vscDarkPlus}>
                {value}
            </SyntaxHighlighter>
        </>
    );
};

export default HtmlPageCodeBlock;
